import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { Common, PopUpBox } from '../../components';
import { styles, options, numberWithNoCommas, numberWithCommas, sw2Alert, alphabetAndNumberOnly } from '../../utils/common';

import { actionSubmitForm22 } from '../../store/application/actions';
import { actionAddressConfig, actionTracking } from '../../store/common/actions';
import DropdownIndicator from '../../components/Common/DropdownIndicator';
import { actionLendelaAppInfo } from '../../store/lendela/action';
import {
  buildConfirmRouteState,
  buildConsentRouteState,
  buildForm21RouteState,
  buildForm22RouteState,
  clearApplicationDraft,
  createApplicationFlowMeta,
  getApplicationDraft,
  saveApplicationDraft,
} from '../../utils/applicationDraft';
import { APPLICATION_FLOW_STEP, buildApplicationFlowPreviousRoute, canMoveApplicationFlowHistoryBack, getApplicationFlowHistoryIndex, isApplicationFlowBackPopState } from '../../utils/applicationFlowBackNavigation';

function ApplicationForm22() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const applicationDraft = getApplicationDraft();
  const flowMeta = createApplicationFlowMeta({
    phone: location.state?.form1?.phone_number || applicationDraft?.meta?.phone,
    lendelaAppId: applicationDraft?.meta?.lendelaAppId,
    noDocLoanData: applicationDraft?.meta?.noDocLoanData,
  });
  const matchedDraft = flowMeta?.phone ? getApplicationDraft(flowMeta) : applicationDraft;
  const getLatestMatchedDraft = () => {
    if (!flowMeta?.phone) {
      return applicationDraft;
    }

    return getApplicationDraft(flowMeta) ?? applicationDraft;
  };
  const restoredState = (!location.state?.form1 || !location.state?.form2) ? buildForm22RouteState(matchedDraft) : null;
  const pageState = location.state ?? restoredState;
  const initialForm22Data = matchedDraft?.form22 ?? pageState?.form3;
  const {
    setValue,
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();
  const [isVisibleInvalidToken, setIsVisibleInvalidToken] = useState(false);
  const [estate, setEstate] = useState('');
  const [addressConfig, setAddressConfig] = useState(null);
  const [optionsDistrict, setOptionsDistrict] = useState([]);
  const [districtValue, setDistrictValue] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const [lendelaData, setLendelaData] = useState(null);
  const [areaDefault, setAreaDefault] = useState(null);
  const [propertyOwnerDefault, setPropertyOwnerDefault] = useState(null);
  const [propertyTypeDefault, setPropertyTypeDefault] = useState(null);
  const [numberLivingWithDefault, setNumberLivingWithDefault] = useState(null);
  const [monthlyRentDefault, setMonthlyRentDefault] = useState(null);
  const skipPersistOnUnmountRef = useRef(false);
  const persistOnUnmountRef = useRef(null);

  useEffect(() => {
    dispatch(
      actionTracking({
        url: window.location.href,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    getAddressConfig();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!location.state?.form1 || !location.state?.form2) {
      if (restoredState?.form1 && restoredState?.form2) {
        navigate('/application-form-2-2', {
          replace: true,
          state: restoredState,
        });
        return;
      }

      if (matchedDraft?.form1 && matchedDraft?.form21) {
        navigate('/application-form-2-1', {
          replace: true,
          state: buildForm21RouteState(matchedDraft),
        });
        return;
      }

      if (flowMeta?.phone) {
        navigate('/application-form-1', {
          replace: true,
          state: buildConsentRouteState(flowMeta),
        });
        return;
      }

      navigate('/');
      return;
    }

    const latestDraft = getLatestMatchedDraft();

    saveApplicationDraft({
      meta: flowMeta,
      currentStep: 'form22',
      form1: latestDraft?.form1 ?? pageState?.form1,
      form21: latestDraft?.form21 ?? pageState?.form2,
      form22: latestDraft?.form22,
    });
  }, [flowMeta?.lendelaAppId, flowMeta?.phone, location.state?.form1, location.state?.form2, navigate, pageState?.form1?.phone_number, pageState?.form2?.job_type, restoredState?.form1, restoredState?.form2]);

  useEffect(() => {
    if (pageState?.lendela_data) {
      setLendelaData(pageState.lendela_data);
      fillLendelaData(pageState.lendela_data);
      return;
    }

    if (!flowMeta?.lendelaAppId) {
      return;
    }

    async function getLendelaInfo() {
      const response = await dispatch(actionLendelaAppInfo(flowMeta.lendelaAppId));
      if (response?.sts_code === 0) {
        const data = response.content;
        setLendelaData(data);
        fillLendelaData(data);
      }
    }

    getLendelaInfo();
  }, [dispatch, flowMeta?.lendelaAppId, pageState?.lendela_data]);

  useEffect(() => {
    const latestDraft = getLatestMatchedDraft();
    const latestForm22Data = latestDraft?.form22 ?? initialForm22Data;

    if (latestForm22Data) {
      hydrateForm22Draft(latestForm22Data);
    }
  }, [matchedDraft?.updatedAt]);

  useEffect(() => {
    if (!addressConfig || !initialForm22Data?.area) {
      return;
    }

    const areaOption = addressConfig?.area?.find((item) => item.value === initialForm22Data.area) ?? null;
    setAreaDefault(areaOption);
    setValue('area', initialForm22Data.area);
    applyDistrictSelection(initialForm22Data.area, initialForm22Data.district_value);
  }, [addressConfig, matchedDraft?.updatedAt, setValue]);

  const getAddressConfig = async () => {
    setLoading(true);
    const response = await dispatch(actionAddressConfig());
    if (response?.content) {
      setAddressConfig(response.content);
    }
    setLoading(false);
  };

  const getDistrictOptionsByArea = (area) => {
    if (!addressConfig) {
      return [];
    }

    switch (area) {
      case 'HK':
        return addressConfig.district_hk ?? [];
      case 'KLN':
        return addressConfig.district_kw ?? [];
      case 'NT':
        return addressConfig.district_nt ?? [];
      default:
        return [];
    }
  };

  const applyDistrictSelection = (area, districtRawValue) => {
    const districtOptions = getDistrictOptionsByArea(area);
    setOptionsDistrict(districtOptions);

    if (!districtRawValue) {
      setDistrictValue(null);
      setValue('district', null);
      return;
    }

    const nextDistrict = districtOptions.find((item) => item.value === districtRawValue) ?? null;
    setDistrictValue(nextDistrict);
    setValue('district', nextDistrict?.value ?? districtRawValue);
  };

  const fillLendelaData = (data) => {
    if (data?.residentialStatus) {
      const propertyOwner = options.property_owner.find((item) => item.lendela_value.includes(data.residentialStatus));
      if (propertyOwner) {
        setValue('property_owner', propertyOwner.value);
        setPropertyOwnerDefault(propertyOwner);
      }
    }
    if (data?.residentialPropertyType) {
      const propertyType = options.property_type.find((item) => item.lendela_value.includes(data.residentialPropertyType));
      if (propertyType) {
        setValue('property_type', propertyType.value);
        setPropertyTypeDefault(propertyType);
      }
    }
  };

  const hydrateForm22Draft = (data) => {
    if (!data) {
      return;
    }

    if (data.area) {
      setValue('area', data.area);
    }

    if (data.addr_line1_room !== undefined) {
      setValue('addr_line1_room', data.addr_line1_room ?? '');
    }

    if (data.addr_line1_floor !== undefined) {
      setValue('addr_line1_floor', data.addr_line1_floor ?? '');
    }

    if (data.addr_line1_block !== undefined) {
      setValue('addr_line1_block', data.addr_line1_block ?? '');
    }

    const estateValue = data.estate ?? data.addr_line2 ?? '';
    setEstate(estateValue);
    setValue('estate', estateValue);

    if (data.property_type) {
      setValue('property_type', data.property_type);
      setPropertyTypeDefault(options.property_type.find((item) => item.value === data.property_type) ?? null);
    }

    if (data.number_living_with) {
      setValue('number_living_with', data.number_living_with);
      setNumberLivingWithDefault(options.number_living_with.find((item) => item.value === data.number_living_with) ?? null);
    }

    if (data.property_owner) {
      setValue('property_owner', data.property_owner);
      setPropertyOwnerDefault(options.property_owner.find((item) => item.value === data.property_owner) ?? null);
    }

    if (data.monthly_rent !== undefined && data.monthly_rent !== null && data.monthly_rent !== '') {
      const monthlyRent = numberWithCommas(data.monthly_rent);
      setMonthlyRentDefault(monthlyRent);
      setValue('monthly_rent', monthlyRent);
    }
  };

  const buildAddressLine1 = (room, floor, block) => {
    let addrLine1 = '';

    if (room) {
      addrLine1 = `RM ${(room ?? '')
        .replace(new RegExp('ROOM', 'g'), '')
        .replace(new RegExp('RM', 'g'), '')
        .replace(new RegExp('FLAT', 'g'), '')
        .replace(new RegExp('FLT', 'g'), '')
        .replace(new RegExp('UNIT', 'g'), '')
        .trim()}`;
    }

    if (floor) {
      addrLine1 += ` ${(floor ?? '')
        .replace(new RegExp('FLOOR', 'g'), '')
        .replace(new RegExp('F', 'g'), '')
        .replace(new RegExp('/F', 'g'), '')
        .trim()}/F`;
    }

    if (block) {
      addrLine1 += ` BLK ${(block ?? '')
        .replace(new RegExp('BLK', 'g'), '')
        .replace(new RegExp('BLOCK', 'g'), '')
        .replace(new RegExp('BL', 'g'), '')
        .trim()}`;
    }

    return addrLine1.trim();
  };

  const buildForm22DraftSnapshot = (baseData = {}) => {
    const currentValues = getValues();
    const currentArea = currentValues?.area ?? baseData?.area;
    const currentDistrictValue = currentValues?.district ?? districtValue?.value ?? baseData?.district_value;
    const currentDistrictOptions = getDistrictOptionsByArea(currentArea);
    const currentDistrict = currentDistrictOptions.find((item) => item.value === currentDistrictValue);
    const currentProvince = addressConfig?.area?.find((item) => item.value === currentArea);
    const room = currentValues?.addr_line1_room ?? baseData?.addr_line1_room ?? '';
    const floor = currentValues?.addr_line1_floor ?? baseData?.addr_line1_floor ?? '';
    const block = currentValues?.addr_line1_block ?? baseData?.addr_line1_block ?? '';
    const estateValue = currentValues?.estate ?? estate ?? baseData?.estate ?? baseData?.addr_line2 ?? '';

    return {
      ...baseData,
      area: currentArea,
      city_id: currentDistrict?.city_id ?? baseData?.city_id,
      province_id: currentProvince?.province_id ?? baseData?.province_id,
      city_id_tu: currentDistrict?.tu_value ?? baseData?.city_id_tu,
      district_value: currentDistrictValue,
      address_config: addressConfig ?? baseData?.address_config,
      addr_line1: buildAddressLine1(room, floor, block) || baseData?.addr_line1 || '',
      addr_line1_room: room,
      addr_line1_floor: floor,
      addr_line1_block: block,
      addr_line2: estateValue,
      estate: estateValue,
      property_type: currentValues?.property_type ?? baseData?.property_type,
      number_living_with: currentValues?.number_living_with ?? baseData?.number_living_with,
      property_owner: currentValues?.property_owner ?? baseData?.property_owner,
      monthly_rent: currentValues?.monthly_rent ? numberWithNoCommas(currentValues.monthly_rent) : (baseData?.monthly_rent ?? ''),
    };
  };

  const persistCurrentForm22Draft = (currentStep = 'form22') => {
    if (!flowMeta?.phone) {
      return null;
    }

    const latestDraft = getLatestMatchedDraft();
    const snapshot = buildForm22DraftSnapshot(latestDraft?.form22 ?? initialForm22Data ?? {});

    saveApplicationDraft({
      meta: flowMeta,
      currentStep,
      form1: latestDraft?.form1 ?? pageState?.form1,
      form21: latestDraft?.form21 ?? pageState?.form2,
      form22: snapshot,
    });

    return snapshot;
  };

  persistOnUnmountRef.current = () => persistCurrentForm22Draft('form22');

  const navigateToPreviousStep = ({ replace = false, useBrowserHistory = false } = {}) => {
    const snapshot = persistCurrentForm22Draft('form21');
    const previousRoute = buildApplicationFlowPreviousRoute({
      step: APPLICATION_FLOW_STEP.FORM22,
      draft: snapshot ?? getLatestMatchedDraft(),
      pageState,
      lendelaData,
    });

    if (!previousRoute) {
      return;
    }

    skipPersistOnUnmountRef.current = true;

    if (useBrowserHistory && canMoveApplicationFlowHistoryBack()) {
      navigate(-1);
      return;
    }

    navigate(previousRoute.pathname, {
      replace,
      state: previousRoute.state,
    });
  };

  const onSubmit = async (data) => {
    const district = getDistrictOptionsByArea(data?.area).find((item) => item.value === districtValue?.value);
    const province = addressConfig.area.find((item) => item.value === data?.area);
    const addrLine1 = buildAddressLine1(data?.addr_line1_room, data?.addr_line1_floor, data?.addr_line1_block);

    if (addrLine1.length > 49 || data?.estate?.length > 49) {
      return sw2Alert('地址資料不可超過50個字元');
    }

    const obj = {
      area: data?.area,
      city_id: district?.city_id,
      province_id: province?.province_id,
      city_id_tu: district?.tu_value,
      addr_line1: addrLine1,
      addr_line1_room: data?.addr_line1_room,
      addr_line1_floor: data?.addr_line1_floor,
      addr_line1_block: data?.addr_line1_block,
      addr_line2: data?.estate,
      estate: data?.estate,
      property_type: data?.property_type,
      number_living_with: data?.number_living_with,
      property_owner: data?.property_owner,
      monthly_rent: numberWithNoCommas(data?.monthly_rent),
    };
    const response = await dispatch(actionSubmitForm22(obj));
    if (response?.sts_code === 0) {
      obj.district_value = districtValue?.value;
      obj.address_config = addressConfig;
      const latestDraft = getLatestMatchedDraft();
      const confirmDraft = {
        form1: latestDraft?.form1 ?? pageState?.form1,
        form21: latestDraft?.form21 ?? pageState?.form2,
        form22: obj,
      };
      skipPersistOnUnmountRef.current = true;

      saveApplicationDraft({
        meta: flowMeta,
        currentStep: 'confirm',
        form1: confirmDraft.form1,
        form21: confirmDraft.form21,
        form22: confirmDraft.form22,
      });

      navigate('/application-confirm-data', {
        state: buildConfirmRouteState(confirmDraft, {
          lendela_data: lendelaData,
        }),
      });
    } else if (response?.sts_code === 3) {
      clearApplicationDraft();
      localStorage.removeItem('access_token');
      localStorage.removeItem('application_phone');
      setIsVisibleInvalidToken(true);
    }
  };

  useEffect(() => {
    const beforeunload = (e) => {
      persistCurrentForm22Draft('form22');
      const event = e || window.event;
      if (event) {
        event.returnValue = 'Sure?';
      }
      return 'Sure?';
    };

    let currentHistoryIndex = getApplicationFlowHistoryIndex();

    const handlePopState = (event) => {
      const shouldMoveToPreviousStep = isApplicationFlowBackPopState(event, currentHistoryIndex);
      currentHistoryIndex = getApplicationFlowHistoryIndex();

      if (shouldMoveToPreviousStep) {
        navigateToPreviousStep({ replace: true });
        return;
      }

      persistCurrentForm22Draft('form22');
    };

    window.addEventListener('beforeunload', beforeunload);
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('beforeunload', beforeunload);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [addressConfig, districtValue, estate, flowMeta, matchedDraft?.form1, matchedDraft?.form21, matchedDraft?.form22, navigateToPreviousStep, pageState?.form1, pageState?.form2]);

  useEffect(() => {
    return () => {
      if (!skipPersistOnUnmountRef.current && persistOnUnmountRef.current) {
        persistOnUnmountRef.current();
      }
    };
  }, []);

  const onClickPrevious = () => {
    navigateToPreviousStep({ useBrowserHistory: true });
  };

  if ((!location.state?.form1 || !location.state?.form2) && restoredState?.form1 && restoredState?.form2) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isVisibleInvalidToken && <PopUpBox.InvalidToken />}
      <Common.Loading isVisible={isSubmitting || isLoading} />
      <div className="bg-white">
        <div className="p-5">
          <Common.StepBar active={1} />
        </div>
        <p className="flex-1 text-2xl text-white bg-primary pl-5 py-2 rounded-tr-full rounded-br-full w-36 font-semibold mb-4">住址資料</p>
        <div className="p-5">
          <div className="mb-6">
            <p className="text-xl text-gray font-bold mb-2">住址資料</p>
            <div className="mb-5">
              <p className="text-gray">區域</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('area', { required: true })}
                  onChange={(e) => {
                    setValue('area', e.value);
                    setError('area', null);
                    setAreaDefault(e);
                    setDistrictValue(null);
                    setValue('district', null);
                    setOptionsDistrict(getDistrictOptionsByArea(e.value));
                  }}
                  options={addressConfig?.area}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={areaDefault}
                />
              </div>
              {errors.area && errors.area.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-5">
              <p className="text-gray font-bold">地區</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('district', { required: true })}
                  value={districtValue}
                  onChange={(e) => {
                    setDistrictValue(e);
                    setValue('district', e?.value);
                    setError('district', null);
                  }}
                  options={optionsDistrict}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                />
              </div>
              {errors.district && errors.district.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-5 flex items-center">
              <div className="flex flex-col pr-1">
                <p className="text-gray font-bold mb-2">單位</p>
                <div className="flex items-center border-b border-primary">
                  <input
                    {...register('addr_line1_room', { required: true })}
                    onChange={(e) => {
                      const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                      setValue('addr_line1_room', value);
                    }}
                    className="flex-1 px-1 outline-none text-primary w-full"
                    placeholder="Room/Flat/Unit"
                  />
                </div>
                {errors.addr_line1_room && errors.addr_line1_room.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
              </div>
              <div className="flex flex-col px-1">
                <p className="text-gray font-bold mb-2">樓層</p>
                <div className="flex items-center border-b border-primary">
                  <input
                    {...register('addr_line1_floor', { required: false })}
                    onChange={(e) => {
                      const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                      setValue('addr_line1_floor', value);
                    }}
                    className="flex-1 px-1 outline-none text-primary w-full"
                    placeholder="Floor"
                  />
                </div>
                {errors.addr_line1_floor && errors.addr_line1_floor.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
              </div>
              <div className="flex flex-col px-1">
                <p className="text-gray font-bold mb-2">座數/大廈</p>
                <div className="flex items-center border-b border-primary">
                  <input
                    {...register('addr_line1_block', { required: false })}
                    onChange={(e) => {
                      const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                      setValue('addr_line1_block', value);
                    }}
                    className="flex-1 px-1 outline-none text-primary w-full"
                    placeholder="Block"
                  />
                </div>
                {errors.addr_line1_block && errors.addr_line1_block.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
              </div>
            </div>
            <div className="mb-5">
              <p className="text-gray font-bold mb-2">屋苑/街道</p>
              <div className="flex items-center border-b border-primary">
                <input
                  {...register('estate', { required: true })}
                  value={estate}
                  onChange={(e) => {
                    const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setEstate(value);
                    setValue('estate', value);
                  }}
                  className="flex-1 px-2 outline-none text-primary w-full"
                  placeholder="(E.g. HAPPY EST)"
                />
              </div>
              {errors.estate && errors.estate.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
          </div>
          <div className="mb-6">
            <p className="text-xl text-gray font-bold mb-2">房屋類型</p>
            <div className="border-b border-primary relative">
              <Select
                placeholder="- 請選擇 -"
                className="w-full"
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 0,
                })}
                styles={styles}
                {...register('property_type', { required: true })}
                onChange={(e) => {
                  setValue('property_type', e.value);
                  setError('property_type', null);
                  setPropertyTypeDefault(e);
                }}
                options={options.property_type}
                isSearchable={false}
                components={{ DropdownIndicator }}
                value={propertyTypeDefault}
                isDisabled={!!propertyTypeDefault && !!lendelaData}
              />
            </div>
            {errors.property_type && errors.property_type.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>
          <div className="mb-6">
            <p className="text-xl text-gray font-bold mb-2">同住人數</p>
            <div className="border-b border-primary relative">
              <Select
                placeholder="- 請選擇 -"
                className="w-full"
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 0,
                })}
                styles={styles}
                {...register('number_living_with', { required: true })}
                onChange={(e) => {
                  setValue('number_living_with', e.value);
                  setError('number_living_with', null);
                  setNumberLivingWithDefault(e);
                }}
                options={options.number_living_with}
                isSearchable={false}
                components={{ DropdownIndicator }}
                value={numberLivingWithDefault}
              />
            </div>
            {errors.number_living_with && errors.number_living_with.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>
          <div className="mb-6">
            <p className="text-xl text-gray font-bold mb-2">持有人</p>
            <div className="border-b border-primary relative">
              <Select
                placeholder="- 請選擇 -"
                className="w-full"
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 0,
                })}
                styles={styles}
                {...register('property_owner', { required: true })}
                onChange={(e) => {
                  setValue('property_owner', e.value);
                  setError('property_owner', null);
                  setPropertyOwnerDefault(e);
                }}
                options={options.property_owner}
                isSearchable={false}
                components={{ DropdownIndicator }}
                value={propertyOwnerDefault}
                isDisabled={!!propertyOwnerDefault && !!lendelaData}
              />
            </div>
            {errors.property_owner && errors.property_owner.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>
          <div className="mb-14">
            <p className="text-xl text-gray font-bold mb-2">每月房租</p>
            <p className="text-xs text-primary">(如沒有住址資料可填 0)</p>
            <Common.InputMoney defaultValue={monthlyRentDefault} setValue={setValue} zeroAllow={true} placeholder=" " register={register} title="monthly_rent" setError={setError} errors={errors} />
          </div>
        </div>
        <Common.FormActionRow
          onBack={onClickPrevious}
          nextLabel="下一步"
          disabled={isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </form>
  );
}

export default ApplicationForm22;
